/**
 * Proposed new time in a meeting response (organizer side of "Propose new time"): shows the proposal and moves the
 * meeting to it on "Accept proposal" (normal update, the attendees get it).
 */
import { useI18n } from '@platform/i18n';
import { Button, toast, useAsync, useErrorText } from '@platform/ui';
import { CalendarClockIcon } from 'lucide-react';
import { useContext, useState } from 'react';
import { CalendarContext } from './calendar-context';
import { api } from '../api';
import { useCalendarFormat } from './format';

export function ProposalNotice({ messageId, eventId }: { messageId: string; eventId: string | null }) {
  const { t } = useI18n();
  const format = useCalendarFormat();
  const zone = useContext(CalendarContext)?.zone;
  const errorText = useErrorText();
  const proposal = useAsync(() => api.meetingProposal(messageId).catch(() => undefined), messageId);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const value = proposal.data;
  if (!value) return null;
  const start = zone?.toWall(value.start) ?? new Date(value.start);
  const end = zone?.toWall(value.end) ?? new Date(value.end);
  const accept = async () => {
    if (!eventId) return;
    setBusy(true);
    try {
      await api.updateEvent(eventId, { start: value.start, end: value.end }, 'occurrence', true);
      toast.success(t('webmail.calendarUx.propose.acceptedToast'));
      setDone(true);
    } catch (error) {
      errorText.notify(error);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 border-t pt-2">
      <CalendarClockIcon aria-hidden className="size-4 text-primary" />
      <span className="min-w-0 flex-1 font-medium">{t('webmail.calendarUx.propose.proposed', { day: format.dayLabel(start), range: format.range(start, end, false) })}</span>
      {eventId && !done && (
        <Button size="sm" disabled={busy} onClick={() => void accept()}>
          {t('webmail.calendarUx.propose.accept')}
        </Button>
      )}
    </div>
  );
}
